import { gql, useMutation } from '@apollo/client'
import cx from 'classnames'
import rollbar from 'helpers/rollbar'
import { useToast } from './Toast'
import { IgnoreResult, IgnoreResultVariables } from './__generated__/IgnoreResult'

const ignoreResultMutation = gql`
  mutation IgnoreResult($id: ID!, $reason: String!, $versionId: ID) {
    ignoreResult(resultId: $id, reason: $reason, versionId: $versionId) {
      result {
        id
        status
      }
    }
  }
`

interface IgnoreMatchButtonProps {
  resultId: string
  versionId?: string
  children: React.ReactNode
  className?: string
}

const IgnoreMatchButton = ({ resultId, versionId, children, className }: IgnoreMatchButtonProps) => {
  const { addToast } = useToast()

  const [ignoreResult, { loading }] = useMutation<IgnoreResult, IgnoreResultVariables>(ignoreResultMutation)

  const onIgnore = async () => {
    const reason = window.prompt(
      'Please explain why this result should not be ignored (e.g. not for one of our customers, already handled manually)'
    )

    if (!reason) {
      return
    }

    try {
      await ignoreResult({
        variables: { id: resultId, reason, versionId },
      })

      addToast({
        messageType: 'success',
        title: 'Ignored',
        content: `Ignored result`,
        timeout: 3000,
      })
    } catch (err) {
      rollbar.error('Error ignoring result', err)
      addToast({
        messageType: 'error',
        title: 'Error',
        content: `Failed to ignore result: ${err.message}`,
      })
    }
  }

  return (
    <button
      type="button"
      className={cx(className, 'button is-small is-outlined is-fullwidth', {
        'is-loading': loading,
      })}
      disabled={loading}
      onClick={onIgnore}
    >
      {children}
    </button>
  )
}

export default IgnoreMatchButton
